import {
  type SignUpCommand,
  SignUpCommandResultStatus,
  postAuthSignUp,
} from "Infrastructure/Api/Api";
import { logError } from "Infrastructure/Utils/LoggingUtils";
import type { NavigateFunction } from "Navigation/useNavigate";
import { LocalStorageKeys } from "Utils/Storage/LocalStorage/LocalStorageKeys";
import { removeLocalStorageItem } from "Utils/Storage/LocalStorage/SecureLocalStorage";
import { getAppUrl } from "Utils/UrlUtils";
import { call, put, takeLatest } from "typed-redux-saga";
import { createAsyncAction, getType } from "typesafe-actions";
import { resetUser } from "State/Auth/AuthReducer";

export const signUpAsync = createAsyncAction(
  "@auth/SIGN_UP_REQUEST",
  "@auth/SIGN_UP_SUCCESS",
  "@auth/SIGN_UP_FAILURE",
)<
  { model: SignUpCommand; navigate: NavigateFunction },
  { login: string },
  Error
>();

function* signUp(action: ReturnType<typeof signUpAsync.request>): Generator {
  try {
    const response = yield* call(postAuthSignUp, action.payload.model);
    if (
      response.status === 200 &&
      (response.data.status === SignUpCommandResultStatus.Success ||
        response.data.status ===
          SignUpCommandResultStatus.AnonymousUserConverted)
    ) {
      yield put(signUpAsync.success({ login: action.payload.model.login }));

      // The intent reached the server — a leftover would leak into a future registration
      // from the same browser.
      removeLocalStorageItem(LocalStorageKeys.RoleIntent);

      // If this was an anonymous user conversion, reset the user state to clear the old anonymous user data
      if (
        response.data.status ===
        SignUpCommandResultStatus.AnonymousUserConverted
      ) {
        yield put(resetUser({}));
      }

      const url = getAppUrl();
      yield* call(() =>
        action.payload.navigate(url("awaiting-email-verification")),
      );
    } else {
      const error = new Error(response.data?.status, {
        cause: response.status,
      });
      yield put(signUpAsync.failure(error));
      logError(error, response, false);
    }
  } catch (err) {
    yield put(signUpAsync.failure(err as Error));
    logError(err as Error, undefined, false);
  }
}
export function* signUpSaga() {
  yield takeLatest(getType(signUpAsync.request), signUp);
}
